import { useCallback, useContext, useEffect, useMemo } from 'react'
import { ScenarioStatesProviderContext } from '../providers/StatesProvider'
import { DialogueModalType } from '../utils/scenario-types'
import useStepApi from './useStepApi'
import useNavigation from './useNavigation'
import { SCENARIO_STEPS } from '../../constants'
import { getStepsWithUnsavedChanges } from '../utils/validation'

const useModal = () => {
    const { step, closeScenario } = useNavigation()
    const context = useContext(ScenarioStatesProviderContext)
    if (context === undefined) {
        throw new Error('useModal must be used within a Scenario States Provider')
    }

    const { updateScenario, patchSatellite, updateHwilConfiguration, patchGroundStations, updateAttitudeCondition, deleteScenario } = useStepApi()
    const { modals, setModals, formData, scenarioResponse, } = context
    const onEdit = useMemo(() => modals?.configured?.onEdit || false, [modals?.configured?.onEdit])

    useEffect(() => {
        if ((modals?.configured?.name === 'satellite' || modals?.configured?.name === 'orbitConfig') && step !== SCENARIO_STEPS.SATELLITES) {
            setModals((prev) => {
                return {
                    ...prev,
                    configured: null
                };
            });
        }
    }, [step, setModals, modals?.configured?.name])

    useEffect(() => {
        const clickOutside = (e: MouseEvent) => {
            if (onEdit || modals?.configured?.name === 'satellite' || modals?.configured?.name === 'orbitConfig') return;
            if (e.target instanceof Element) {
                const modalContainers = document.getElementsByClassName('custom__modal__container__wrapper');
                const modalTriggers = document.getElementsByClassName('modal__trigger__button');

                const totalElements = [...Array.from(modalContainers), ...Array.from(modalTriggers)];
                let clickedInside = false;
                const filterElements = totalElements.filter((element) => {
                    return !element.classList.contains('scenario__create__modal')
                })
                for (let i = 0; i < filterElements.length; i++) {
                    if (filterElements[i].contains(e.target as Node)) {
                        clickedInside = true;
                        break;
                    }
                }

                if (clickedInside) return;
                setModals((prev) => {
                    return {
                        ...prev,
                        configured: null
                    };
                });
            }
        }
        const canvasClickHandler = (e: any) => {
            const canvas = document.querySelector("canvas");
            if (canvas && canvas.contains(e.target)) {
                clickOutside(e);
            }
        };
        document.addEventListener("click", canvasClickHandler);
        document.addEventListener('mousedown', clickOutside)
        return () => {
            document.removeEventListener('mousedown', clickOutside)
            document.removeEventListener("click", canvasClickHandler);
        }
    }, [setModals, onEdit, modals])

    const getFunction = useCallback(async (step: string) => {
        switch (step) {
            case SCENARIO_STEPS.DETAILS:
                return await updateScenario()
            case SCENARIO_STEPS.SATELLITES:
                return await patchSatellite()
            case SCENARIO_STEPS.INITIAL_ALTITUDE:
                return await updateAttitudeCondition()
            case SCENARIO_STEPS.HWIL:
                return await updateHwilConfiguration()
            case SCENARIO_STEPS.SEGMENTS:
                return await patchGroundStations()
            default:
                return () => { }
        }
    }, [step, updateScenario, patchSatellite, updateAttitudeCondition, updateHwilConfiguration, patchGroundStations])

    const openConfirmationDialouge = useCallback(() => {
        const modalDetails = {
            type: 'cancel' as DialogueModalType['type'],
            title: 'Data will be lost',
            description: 'Updating configurations will result in reset of the data and generated files.',
            onConfirm: () => {
                getFunction(step as string)
                setModals((prev) => ({
                    ...prev,
                    dialouge: null
                }))
            },
            onCancel: () => {
                setModals((prev) => ({
                    ...prev,
                    dialouge: null
                }))
            },
            confirm_button_text: 'Confirm',
        }

        setModals((prev) => ({
            ...prev,
            dialouge: modalDetails
        }))
    }, [step, getFunction, setModals])

    const discardScenario = async () => {
        setModals((prev) => ({
            ...prev,
            dialouge: {
                ...prev.dialouge!,
                loading: true
            }
        }))
        await deleteScenario()
    }

    const onCloseScenario = useCallback((page: "Home" | "Dashboard") => {
        const unSavedChanges = getStepsWithUnsavedChanges(formData, scenarioResponse)
        if (unSavedChanges || scenarioResponse) {
            const draftMode = scenarioResponse?.draftMode === 'Yes'
            const modalDetails = {
                type: draftMode ? 'discard' : 'cancel' as DialogueModalType['type'],
                title: draftMode ? 'Discard Scenario Creation?' : 'Close Scenario',
                description: draftMode ? 'Discarding this draft will result in the loss of all scenario data.' : 'Are you sure you want to close the scenario?',
                onDiscard: draftMode ? discardScenario : undefined,
                onConfirm: () => {
                    closeScenario(page)
                },
                onCancel: () => {
                    setModals((prev) => ({
                        ...prev,
                        dialouge: null
                    }))
                },
                cancel_button_text: draftMode ? 'Discard' : 'Cancel',
                confirm_button_text: draftMode ? 'Save As Draft' : 'Confirm',
            }
            setModals((prev) => ({
                ...prev!,
                dialouge: modalDetails
            }))
        } else {
            closeScenario(page)
        }
    }, [setModals, closeScenario, discardScenario, formData, scenarioResponse])


    return { modals, setModals, openConfirmationDialouge, onCloseScenario }
}

export default useModal